<template>
    <div
        class="output-side-nav"
        :class="[
            { 'output-side-nav--collapse': collapse },
            { 'output-side-nav--sticky-fix': props.type !== 'pa' },
        ]"
    >
        <div
            class="output-side-nav__top"
            :class="{ 'output-side-nav__top--sticky-fix': props.type !== 'pa' }"
        >
            <div
                v-if="props.type !== 'pa'"
                class="output-side-nav__target-wrapper pa-4"
                :class="{ 'output-side-nav__target-wrapper--cursor': collapse }"
                @click="collapse ? (collapse = false) : (collapse = false)"
            >
                <div
                    class="output-side-nav__target-entity display-flex flex-wrap pa-5 bg-moat-900 align-space-between flex-column"
                >
                    <div class="output-side-nav__title-holder display-flex align-items-center">
                        <font-awesome-icon
                            :icon="`fas fa-bullseye`"
                            class="output-side-nav__icon text-stone-0 mr-3 target-icon"
                            size="lg"
                        />
                        <h6 class="output-side-nav__tab-title text-stone-0">Target Company</h6>
                    </div>
                    <p class="output-side-nav__tab-desc text-stone-0 mt-2 mb-5 w-full">
                        Select your target company to focus your analysis. Gain detailed insights
                        into their competitive landscape and innovation footprint.
                    </p>
                    <div
                        v-if="
                            arenaOutputStore.targetCompany?.length > 0 &&
                            arenaOutputStore.targetCompany[0]
                        "
                        class="output-side-nav__company-details-holder w-full"
                    >
                        <AonSpinner
                            v-if="arenaOutputStore.targetCompanyLoading"
                            color="primary"
                            class="ml-auto mr-auto mt-3"
                            :scale="0.3"
                        />
                        <div v-else class="output-side-nav__company-details">
                            <p class="extra-large text-truncate text-stone-0 bold">
                                {{ arenaOutputStore.targetCompany[0].aonEntityName }}
                            </p>
                            <p class="bold text-stone-0 mt-5">Patent Count</p>
                            <p class="extra-large text-truncate text-stone-0 mt-2">
                                {{ arenaOutputStore.targetCompany[0].patentCount.toLocaleString() }}
                            </p>
                            <p class="bold text-stone-0 mt-5">Avg. Moat Coverage</p>
                            <p class="extra-large text-truncate text-stone-0 mt-2">
                                {{ calcPowerScore.toFixed(2) }}
                            </p>
                        </div>
                    </div>
                    <AonButton
                        class="output-side-nav__target-button"
                        label="Change Target Company"
                        :class="{ disabled: arenaOutputStore.targetCompanyLoading }"
                        :text-with-icon="true"
                        :icon-options="{ iconStyle: 'fas', iconName: 'fa-arrow-up-arrow-down' }"
                        @clicked="toggleTargetCompanyModal()"
                    />
                </div>
            </div>
            <div class="output-side-nav__focus-tabs display-flex flex-column">
                <div
                    v-for="tab in focusTabs"
                    class="output-side-nav__tab py-3 px-4"
                    style="position: relative"
                    :class="[
                        { 'output-side-nav__tab--active': tab.tabNum === activeTab },
                        { disabled: techAreasLoading && tab.tabNum === 2 },
                    ]"
                    @click="setActiveTab(tab.tabNum)"
                >
                    <div
                        class="output-side-nav__tab-content display-flex align-items-center flex-wrap"
                    >
                        <div
                            class="output-side-nav__header display-flex align-items-center w-full justify-between"
                        >
                            <div
                                class="output-side-nav__title-holder display-flex align-items-center"
                            >
                                <font-awesome-icon
                                    :icon="`fas ${tab.icon}`"
                                    class="output-side-nav__icon text-stone-800 mr-3"
                                    size="lg"
                                />
                                <h6 class="output-side-nav__tab-title text-stone-800">
                                    {{ tab.title }}
                                </h6>
                            </div>
                            <font-awesome-icon
                                v-if="tab.tabNum === 2 && showWarning"
                                v-tooltip="{
                                    content:
                                        'Changes made in Technology Areas will effect the Arena Space',
                                }"
                                :icon="`fas fa-circle-info`"
                                class="output-side-nav__notification-icon castle-moat-hover--text mr-3"
                                size="lg"
                            />
                        </div>
                        <AonSpinner
                            v-if="techAreasLoading && tab.tabNum === 2"
                            color="castleMoat"
                            class="ml-auto mr-auto my-1"
                            :scale="0.3"
                        />
                        <div v-else class="output-side-nav__desc-holder">
                            <p class="output-side-nav__tab-desc text-stone-800 mt-2">
                                {{ tab.desc }}
                            </p>
                            <div
                                v-if="tab.type"
                                class="output-side-nav__tab-pill bg-stone-400 px-3 py-1 mt-3"
                            >
                                <p class="bold text-stone-0">{{ computedSelections }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="output-side-nav__bottom w-full display-flex align-items-center px-2 py-1 grey04"
            @click="toggleCollapse()"
        >
            <div
                class="output-side-nav__handle display-flex justify-center align-items-center w-full pl-5"
            >
                <font-awesome-icon
                    icon="fas fa-arrow-left-to-bracket"
                    class="output-side-nav__icon text-stone-800 mr-5"
                    size="lg"
                />
            </div>
            <h6 class="output-side-nav__collapse-text text-stone-800">Collapse Sidebar</h6>
        </div>

        <div v-if="arenaOutputStore.showTargetCompanyModal" class="output-side-nav__modal-holder">
            <AonModal title="" message="" col-width="8" @close="toggleTargetCompanyModal()">
                <div class="output-side-nav__selected-competitors py-5">
                    <SearchTable
                        class="my-n5 output-side-nav__search-table"
                        type="company"
                        @comp-selected="assignCompetitor"
                    />
                    <div
                        class="output-side-nav__action-holder display-flex align-items-end w-full mt-8"
                    >
                        <AonButton
                            class="mx-5"
                            :class="{
                                disabled:
                                    arenaOutputStore.newTargetCompany.length > 1 ||
                                    arenaOutputStore.newTargetCompany.length === 0,
                            }"
                            label="Apply"
                            :loading="patchLoading"
                            @clicked="applyCompany()"
                        />
                        <AonButton
                            label="Cancel"
                            type="subtle"
                            @clicked="toggleTargetCompanyModal()"
                        />
                    </div>
                </div>
            </AonModal>
        </div>
    </div>
</template>

<script setup>
import { ref, inject, computed, watch, nextTick, onUnmounted } from 'vue'
import { useArenaOutputStore, useMoat2ProductStore } from '@/stores'
import { patchArena } from '@/api/arena.js'

import SearchTable from '@/components/CompaniesSearchTable'

import { useI18n } from 'vue-i18n'

const emit = defineEmits(['everything-done-loading', 'reset-arena-output'])
const eventBus = inject('eventBus')
const logger = inject('logger')
const { t } = useI18n()
const arenaOutputStore = useArenaOutputStore()
const moat2ProductStore = useMoat2ProductStore()

const props = defineProps({
    type: {
        type: String,
        default: 'pa',
    },
    peersEnabled: {
        type: Boolean,
        default: false,
    },
    techAreasLoading: {
        type: Boolean,
        default: true,
    },
})

const totalTechAreas = ref(0)
const patchLoading = ref(false)
const collapse = ref(false)
const focusTabs = ref([
    {
        tabNum: 1,
        title: t('reportOutput.sideNav.tab1.title'),
        desc: t('reportOutput.sideNav.tab1.desc'),
        icon: 'fa-microchip',
        type: 'tech',
    },
    {
        tabNum: 2,
        title: t('reportOutput.sideNav.tab2.title'),
        desc: t('reportOutput.sideNav.tab2.desc'),
        icon: 'fa-ranking-star',
        // type: 'arena',
    },
])

watch(
    () => props.techAreasLoading,
    (newVal, oldVal) => {
        if (newVal !== oldVal) {
            setTimeout(() => {
                getTotalTechAreas()
            }, 1000)
        }
    }
)

const error = ref(null)

const calcPowerScore = computed((type) => {
    if (arenaOutputStore.targetCompany.length > 0 && !arenaOutputStore.targetCompanyLoading) {
        let score =
            arenaOutputStore.targetCompany[0].powerScore /
            arenaOutputStore.targetCompany[0].patentCount
        return score ? score : 0
    } else {
        return 0
    }
})

const activeTab = computed((type) => {
    return props.type === 'pa'
        ? moat2ProductStore.productAlignmentActiveTab
        : arenaOutputStore.arenaOutputActiveTab
})

const showWarning = computed((type) => {
    if (props.type === 'pa') {
        return (
            moat2ProductStore.productAlignmentActiveTab === 1 &&
            !setsAreEqual(
                moat2ProductStore.productAlignmentTechAreaSelectionsOld,
                moat2ProductStore.productAlignmentTechAreaSelections
            ) &&
            moat2ProductStore.productAlignmentTechAreaSelectionsOld.size > 0
        )
    } else {
        return (
            arenaOutputStore.arenaOutputActiveTab === 1 &&
            !setsAreEqual(
                arenaOutputStore.arenaOutputTechAreaSelectionsOld,
                arenaOutputStore.arenaOutputTechAreaSelections
            ) &&
            arenaOutputStore.arenaOutputTechAreaSelectionsOld.size > 0
        )
    }
})

const getTotalTechAreas = () => {
    if (props.type === 'pa') {
        totalTechAreas.value = moat2ProductStore.techAreaIdCollection.length
    } else {
        totalTechAreas.value = arenaOutputStore.techAreaIdCollection.length
    }
}

const computedSelections = computed((type) => {
    if (props.type === 'pa') {
        return `${moat2ProductStore.techAreaIdCollection.length} of ${totalTechAreas.value} Technology Areas Selected`
    } else {
        return `${arenaOutputStore.techAreaIdCollection.length} of ${totalTechAreas.value} Technology Areas Selected`
    }
})

const toggleCollapse = (forceClose = false) => {
    return (collapse.value = !collapse.value)
}

const toggleTargetCompanyModal = () => {
    if (arenaOutputStore.showTargetCompanyModal) {
        arenaOutputStore.newTargetCompany = []
    }
    arenaOutputStore.showTargetCompanyModal = !arenaOutputStore.showTargetCompanyModal
}

const setActiveTab = (tabNum) => {
    if (props.type === 'pa') {
        moat2ProductStore.productAlignmentActiveTab = tabNum
        setTimeout(() => {
            moat2ProductStore.productAlignmentTechAreaSelectionsOld =
                moat2ProductStore.productAlignmentTechAreaSelections
        }, 1000)
    } else {
        arenaOutputStore.arenaOutputActiveTab = tabNum
        setTimeout(() => {
            arenaOutputStore.arenaOutputTechAreaSelectionsOld =
                arenaOutputStore.arenaOutputTechAreaSelections
        }, 1000)
    }
}

const assignCompetitor = (comp) => {
    const index = arenaOutputStore.newTargetCompany.findIndex((r) => r.name === comp.name)
    if (index > -1) {
        arenaOutputStore.newTargetCompany.splice(index, 1)
    } else {
        arenaOutputStore.newTargetCompany.push(comp)
    }
}

const applyCompany = async () => {
    patchLoading.value = true
    try {
        var { data } = await patchArena(arenaOutputStore.alignmentPk, {
            EntityPk: arenaOutputStore.newTargetCompany[0].aon_entity_pk,
        })
    } catch (err) {
        handleError('And error occurred')
        error.value = true
    } finally {
        patchLoading.value = false
        arenaOutputStore.targetCompanyLoading = true
        emit('reset-arena-output')
        toggleTargetCompanyModal()
        arenaOutputStore.targetCompany = [
            { aonEntityPk: data.entityPk, aonEntityName: '', patentCount: 0, powerScore: 0 },
        ]
        setTimeout(() => {
            arenaOutputStore.arenaOutputActiveTab = 2
            arenaOutputStore.initLoad = true
        }, 1000)
    }
}

const handleError = (err) => {
    logger.error(err)

    eventBus.emit('snacktime', {
        type: 'error',
        message: err,
    })

    error.value = true
}

const setsAreEqual = (s1, s2) => {
    return s1.size === s2.size && [...s1].every((x) => s2.has(x))
}
</script>

<style lang="scss" scoped>
.output-side-nav {
    width: 425px;
    min-width: 425px;
    min-height: 100vh;
    background: var(--color-stone-300);
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: var(--transition-all-normal);

    @media screen and (max-height: 950px) {
        &--collapse {
            overflow: hidden;
        }
    }

    &--sticky-fix {
        @media screen and (max-height: 950px) {
            overflow: auto;

            &.output-side-nav--collapse {
                overflow: hidden !important;
            }

            .output-side-nav__top {
                top: 0 !important;
                position: static;
            }

            .output-side-nav__bottom {
                position: static;
            }
        }
    }

    @media screen and (max-height: 600px) {
        overflow: auto;

        &__bottom {
            position: static;
        }

        &__top {
            top: 0 !important;
        }
    }

    &__target-wrapper {
        border-bottom: var(--border-width-thin) solid var(--color-border);

        &--cursor {
            cursor: pointer;
        }
    }

    &__target-entity {
        border-radius: var(--border-radius-lg);
        overflow: hidden;
        transition: var(--transition-all-normal);
    }

    &__icon {
        transition: var(--transition-all-normal);

        &.target-icon {
            transform: translate(0, 0);
        }
    }

    &__company-details-holder {
        min-height: 200px;
    }

    &__top {
        position: sticky;
        top: 0;

        &--sticky-fix {
            position: relative;
        }
    }

    &__company-details-holder,
    &__target-button,
    &__tab-title,
    &__tab-desc,
    &__tab-pill {
        transform: translateX(0);
        opacity: 1;
        pointer-events: all;
        transition: var(--transition-all-normal);
    }

    &__tab-pill {
        border-radius: var(--border-radius-full);
    }

    &__tab {
        border-left: var(--border-width-thick) solid transparent;
        cursor: pointer;
        transition: var(--transition-all-normal);

        &:hover {
            background: var(--color-background-hover);
        }

        &--active {
            background: var(--color-background-active);
            border-color: var(--color-primary);
        }

        &__content {
            min-width: 390px;
            overflow: hidden;
        }
    }

    &__bottom {
        border-top: var(--border-width-thin) solid var(--color-border-light);
        cursor: pointer;
        position: sticky;
        bottom: 0;
    }

    &__collapse-text {
        min-width: 370px;
        transform: translateX(0);
        opacity: 1;
        pointer-events: all;
        transition: var(--transition-all-normal);
    }

    &__handle {
        height: 40px;
        width: 40px;
        cursor: pointer;
        transition: var(--transition-all-normal);

        .output-side-nav__icon {
            transform: rotate(0);
            transition: var(--transition-all-normal);
        }
    }

    &--collapse {
        width: 60px !important;
        min-width: 60px !important;

        .output-side-nav__tab-content {
            pointer-events: none;
            height: 32px;

            .output-side-nav__icon {
                padding-top: 6px;
            }
        }

        .output-side-nav__target-entity {
            height: 25px;
            padding: 0 !important;
            background: transparent;

            .output-side-nav__icon.target-icon {
                transform: translate(4px, -10px);
                color: var(--color-text-tertiary) !important;
            }
        }

        .output-side-nav__tab-title,
        .output-side-nav__tab-desc,
        .output-side-nav__tab-pill,
        .output-side-nav__company-details-holder,
        .output-side-nav__target-button {
            transform: translateX(-50px);
            opacity: 0;
            pointer-events: none;
        }

        .output-side-nav__handle {
            .output-side-nav__icon {
                transform: rotate(180deg);
            }
        }

        .output-side-nav__collapse-text {
            min-width: 0;
            transform: translateX(-50px);
            opacity: 0;
            pointer-events: none;
        }
    }

    &__search-table {
        height: 72vh;
        max-height: 725px;
    }

    &__modal-holder {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;

        :deep(.a-modal) {
            .container {
                padding: 0 !important;
                border-radius: var(--border-radius-lg) !important;
                max-height: 800px !important;
                height: 80% !important;

                .output-side-nav__selected-competitors {
                    height: calc(100% - 210px);
                }
            }
        }

        :deep(.disabled-checkbox) {
            .ag-selection-checkbox {
                opacity: 0.2;
                pointer-events: none;
            }
        }
    }
}
</style>
